import { useEffect, useRef, useState } from "react";
import { api, type RemediationRunStatus, type RemediationReport } from "../api/client";

const POLL_MS = 2000;

function fmtHealth(h: number | null | undefined): string {
  return h == null ? "?" : String(h);
}

function clampPct(v: number | null | undefined, target: number): number {
  if (v == null || !target || target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((v / target) * 100)));
}

export function HealthHero({
  before,
  after,
  target = 100,
  running = false,
  round,
}: {
  before: number | null | undefined;
  after: number | null | undefined;
  target?: number;
  running?: boolean;
  round?: number;
}) {
  const beforeText = before == null ? "—" : String(before);
  const afterText = after == null ? "—" : String(after);
  return (
    <div className="health-hero" data-testid="health-hero">
      <span className="health-hero__label">Code health</span>
      <div className="health-hero__scores">
        <strong className="health-hero__before" data-testid="health-hero-before">
          {beforeText}
        </strong>
        <span className="health-hero__arrow" aria-hidden="true">
          ──►
        </span>
        <strong className="health-hero__after" data-testid="health-hero-after">
          {afterText}
        </strong>
        <span className="health-hero__target">/ {target}</span>
      </div>
      <div className="health-hero__bars" aria-hidden="true">
        <div className="health-hero__bar">
          <div
            className="health-hero__bar-fill"
            style={{ width: `${clampPct(before, target)}%` }}
          />
        </div>
        <span className="health-hero__bar-arrow">→</span>
        <div className="health-hero__bar health-hero__bar--after">
          <div className="health-hero__bar-fill" style={{ width: `${clampPct(after, target)}%` }} />
        </div>
      </div>
      {running ? <p className="health-hero__status">● running… · Round {round ?? 0}</p> : null}
    </div>
  );
}

function fmtCost(cost: RemediationReport["cost"]): string {
  if (!cost) return "Cost: not metered.";
  const dollars =
    cost.cents != null ? `$${(cost.cents / 100).toFixed(2)}` : "$ — (agent on user account)";
  return `Cost: ${cost.rounds}r · ${cost.sends} sends · ${(cost.elapsedMs / 1000).toFixed(1)}s · ${cost.tokensIn}/${cost.tokensOut} tok · ${dollars}`;
}

export function outcomeLabel(o: RemediationReport["outcome"]): string {
  if (o.cleanAchieved) return "clean ✅";
  if (o.terminationReason === "stable") return "stable";
  return `capped (${o.terminationReason})`;
}

export function Report({
  report,
  onOpenBranch,
}: {
  report: RemediationReport;
  onOpenBranch: (b: string) => void;
}) {
  const s = report.summary;
  const healthStr =
    s.finalHealth == null
      ? "health unavailable"
      : `health ${fmtHealth(s.startHealth)}→${s.finalHealth}/100`;
  return (
    <div className="remediation-report" data-testid="remediation-report">
      <HealthHero before={s.startHealth} after={s.finalHealth} target={100} running={false} />
      <p className="remediation-outcome">
        <strong>{outcomeLabel(report.outcome)}</strong> — {healthStr} over {s.totalRounds} round(s)
        {report.branch ? (
          <>
            {" "}
            · branch <code>{report.branch}</code>
          </>
        ) : null}
      </p>
      {report.outcome.error && !report.outcome.cleanAchieved && (
        <p className="remediation-error" data-testid="remediation-outcome-error">
          {report.outcome.terminationReason === "setup-failed" ? "Setup failed — " : "Run error — "}
          {report.outcome.error}
        </p>
      )}
      {report.progression.length > 0 && (
        <details className="remediation-breakdown">
          <summary>Per-round breakdown</summary>
          <table className="remediation-progression">
            <thead>
              <tr>
                <th>Round</th>
                <th>Health</th>
                <th>Δ</th>
                <th>Fixed</th>
                <th>Introduced</th>
                <th>Verify</th>
              </tr>
            </thead>
            <tbody>
              {report.progression.map((p) => (
                <tr key={p.round}>
                  <td>{p.round}</td>
                  <td>
                    {p.healthBefore}→{p.healthAfter}
                  </td>
                  <td>{p.delta >= 0 ? `+${p.delta}` : p.delta}</td>
                  <td>{p.fixedIds.length}</td>
                  <td>{p.introducedIds.length}</td>
                  <td>{p.verifyPassed ? "✅" : "❌"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <p className="remediation-findings">
        Fixed ({s.fixedCount}): {s.fixedIds.length ? s.fixedIds.join(", ") : "none"}
        {" · "}
        {s.remainingIds.length
          ? `Remaining (${s.remainingCount}): ${s.remainingIds.join(", ")}`
          : "Remaining: none ✅"}
      </p>
      <p className="remediation-cost">{fmtCost(report.cost)}</p>
      <p className="remediation-gated">
        Gated actions:{" "}
        {report.gatedActions.length
          ? report.gatedActions
              .map((g) => `${g.command} — ${g.pack}/${g.rule} [${g.decision}]`)
              .join("; ")
          : "None"}
      </p>
      {report.branch && (
        <button
          className="button secondary"
          type="button"
          onClick={() => onOpenBranch(report.branch!)}
        >
          Open in terminal
        </button>
      )}
    </div>
  );
}

export function RemediationRunPanel({
  runId,
  onOpenBranch,
  onSettled,
  pollMs = POLL_MS,
}: {
  runId: string;
  onOpenBranch: (branch: string) => void;
  onSettled?: () => void;
  pollMs?: number;
}) {
  const [status, setStatus] = useState<RemediationRunStatus | null>(null);
  const [expired, setExpired] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const sawRunning = useRef(false);
  // Keep the latest onSettled without making it a poll-effect dep (which would
  // restart polling on every parent render).
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    setStatus(null);
    setExpired(false);
    setNotFound(false);
    sawRunning.current = false;

    async function poll() {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const s = await api.getRemediationRunStatus(runId);
        if (cancelled) return;
        setStatus(s);
        if (s.state === "running") sawRunning.current = true;
        if (s.state === "done" || s.state === "error") {
          onSettledRef.current?.();
          return; // terminal — stop
        }
      } catch (err) {
        if (cancelled) return;
        const code = (err as { status?: number })?.status;
        // ANY 404 is terminal — stop polling (Gemini #142): if we'd seen it
        // running, the host just evicted the finished run (expired); otherwise
        // the run id is unknown (not found). Either way, never loop forever.
        if (code === 404) {
          if (sawRunning.current) setExpired(true);
          else setNotFound(true);
          onSettledRef.current?.();
          return;
        }
        // other (transient) error → keep polling on the next tick
      } finally {
        inFlight = false;
      }
      if (!cancelled) timer = setTimeout(poll, pollMs);
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, pollMs]);

  return (
    <div className="remediation-run-panel" data-testid="remediation-run-panel">
      {notFound ? (
        <p className="remediation-error" data-testid="remediation-not-found">
          Run not found.
        </p>
      ) : expired ? (
        <p className="remediation-note">Run completed — live status expired.</p>
      ) : !status || status.state === "running" ? (
        <div className="remediation-running" data-testid="remediation-running">
          {/* The live status only carries the current (post-round) health
              (runService sets status.health = healthAfter), so the true
              baseline isn't known mid-run — show "—" for "before" until the
              report lands with the authoritative startHealth→finalHealth. */}
          <HealthHero
            before={null}
            after={status?.health ?? null}
            target={100}
            running
            round={status?.round ?? 0}
          />
        </div>
      ) : status.state === "error" ? (
        <p className="remediation-error" data-testid="remediation-error">
          Remediation failed: {status.error || "unknown error"}
        </p>
      ) : status.report ? (
        <Report report={status.report} onOpenBranch={onOpenBranch} />
      ) : (
        <p className="remediation-note">Run complete.</p>
      )}
    </div>
  );
}
